import React, { useState } from "react";
import { z } from "zod";
import { useAuth } from "@/hooks/useAuth";
import { Config } from "@/config/Config";
import { Input } from "@/components/common/Input";
import { Button } from "@/components/common/Button";
import { Shield, Lock, AlertCircle, Key, Server } from "lucide-react";

const loginSchema = z.object({
  username: z.string().min(1, "E-mail ou nome de usuário é obrigatório"),
  password: z
    .string()
    .min(1, "A senha é obrigatória")
    .max(72, "A senha não pode exceder 72 caracteres"),
});

export const LoginPage: React.FC = () => {
  const { login } = useAuth();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrors({});
    setErrorMessage(null);

    const result = loginSchema.safeParse({ username, password });
    if (!result.success) {
      const fieldErrors: Record<string, string> = {};
      result.error.issues.forEach((issue) => {
        const field = issue.path[0] as string;
        if (!fieldErrors[field]) {
          fieldErrors[field] = issue.message;
        }
      });
      setErrors(fieldErrors);
      return;
    }

    setIsSubmitting(true);
    try {
      await login(username, password);
    } catch (err: any) {
      setErrorMessage(err.message || "Falha na autenticação. Verifique as credenciais.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="login-wrapper">
      <div className="login-card">
        <div className="login-header">
          <div className="login-logo">
            <Shield size={36} className="text-primary" />
          </div>
          <h1 className="login-title">Painel Administrativo IAM</h1>
          <p className="login-subtitle">Gerenciamento Multi-Tenant da Auth API</p>
        </div>

        {errorMessage && (
          <div className="alert-box alert-danger" role="alert" data-testid="login-error-alert">
            <AlertCircle size={18} />
            <span>{errorMessage}</span>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <Input
            id="username-input"
            label="Usuário ou E-mail"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            error={errors.username}
            placeholder="admin@dominio.com.br"
            required
            autoComplete="username"
          />

          <Input
            id="password-input"
            type="password"
            label="Senha de Acesso"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            error={errors.password}
            placeholder="••••••••"
            required
            autoComplete="current-password"
          />

          <div className="pt-2">
            <Button
              type="submit"
              variant="primary"
              className="w-full btn-lg"
              isLoading={isSubmitting}
            >
              <Key size={18} />
              <span>Entrar no Painel</span>
            </Button>
          </div>
        </form>

        <div className="login-security-notice">
          <div className="flex items-center gap-2 text-xs text-muted">
            <Lock size={14} />
            <span>Sessão em memória • Cookie HttpOnly Strict • Rate Limit Ativo</span>
          </div>
          <div className="flex items-center gap-2 text-xs text-muted mt-1">
            <Server size={14} />
            <span>API: {Config.authApiUrl}</span>
          </div>
        </div>
      </div>
    </div>
  );
};
